import { CalendarToday } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  ButtonGroup,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { SVG } from 'components/Asset/SVG';
import React, { useMemo, useState } from 'react';
import { type HealthScore } from 'types/squares.type';
import { type ServiceView } from 'types/survivalAnalysis.types';
import { ASSET_TYPES } from 'utils/enums';
import { timeAgo } from 'views/FleetOverview/components/AssetCardsV2/utils';
import dozerImage from 'views/SurvivalAnalysis/Helper/Icons/dozer.png';
import { GetIcons } from 'views/SurvivalAnalysis/Helper/Icons/GetIcons.function';
import graderImage from 'views/SurvivalAnalysis/Helper/Icons/grader.png';
import haulerImage from 'views/SurvivalAnalysis/Helper/Icons/haultruck.png';
import lightTruckImage from 'views/SurvivalAnalysis/Helper/Icons/lighttruck.png';
import loaderImage from 'views/SurvivalAnalysis/Helper/Icons/loader.png';
import { ThemePalette } from 'views/SurvivalAnalysis/Helper/Icons/theme.squares';
import assetImage from 'views/SurvivalAnalysis/Helper/Icons/vehicle.png';
import HealthScorePie from '../Helpers/HealthScore.Pie';
import AssetDetailsTable from './AssetDetailsTable';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { healthScorePieRange } from '../constants/squares.dashboard.constants';
import { useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface AssetTableProps {
  assets: HealthScore[];
  filteredAssets: ServiceView[];
  enableDarkTheme: boolean;
  onShowDetails: (itemId: string) => void;
  setShowAssetDetails: (param: boolean) => void;
  selectedMonth: string | null;
}

// constants
export const SORT_OPTIONS = [
  {
    id: 'Manufacturer Date',
    display: 'Manufacturer Rec. Date',
  },
  {
    id: 'Squares Date',
    display: 'System Rec. Date',
  },
  {
    id: 'Health Score',
    display: 'Health Score',
  },
];

const filterOptions: Array<{
  label: string;
  value: 'ALL' | 'Connected' | 'Disconnected';
}> = [
  { label: 'ALL', value: 'ALL' },
  { label: 'Live Data available', value: 'Connected' },
  { label: 'Live Data Unavailable', value: 'Disconnected' },
];

// helpers

const parseSelectedMonth = (selectedMonth: string) => {
  const [monthName, year] = selectedMonth.split(' ');
  const monthIndex = new Date(Date.parse(`${monthName} 1, ${year}`)).getMonth();
  return { monthIndex, year: Number(year) };
};

const compareDates = (dateA: string, dateB: string) => {
  const parsedDateA = new Date(dateA).getTime();
  const parsedDateB = new Date(dateB).getTime();

  if (isNaN(parsedDateA) && isNaN(parsedDateB)) return 0;
  if (isNaN(parsedDateA)) return 1;
  if (isNaN(parsedDateB)) return -1;

  return parsedDateB - parsedDateA;
};

const compareNumbers = (numA: any, numB: any) => {
  const parsedNumA = Number(numA);
  const parsedNumB = Number(numB);

  if (isNaN(parsedNumA) && isNaN(parsedNumB)) return 0;
  if (isNaN(parsedNumA)) return 1;
  if (isNaN(parsedNumB)) return -1;

  return parsedNumB - parsedNumA;
};

const getAssetImage = (arg: string) => {
  if (arg === 'light_truck') {
    return lightTruckImage;
  }
  if (arg === 'haul_truck') {
    return haulerImage;
  }
  if (arg === 'loader') {
    return loaderImage;
  }
  if (arg === 'dozer') {
    return dozerImage;
  }
  if (arg === 'grader') {
    return graderImage;
  }
  return assetImage;
};

const AssetTable: React.FC<AssetTableProps> = ({
  assets,
  filteredAssets,
  enableDarkTheme,
  onShowDetails,
  selectedMonth,
  setShowAssetDetails,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const partsHealthComponent = useAppSelector(
    (state) => state.squaresPartsRecommendationsReducer.healthData
  );
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [arrowDirection, setArrowDirection] = useState<Record<number, boolean>>(
    {}
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('Manufacturer Date');
  const [filterOption, setFilterOption] = useState<
    'ALL' | 'Connected' | 'Disconnected'
  >('ALL');

  const getLastConnected = (id: string) => {
    const asset = filteredAssets.find((asset) => asset.item_id === id);
    return asset ? asset.lastConnected : null;
  };
  const handleRowClick = (index: number) => {
    setExpandedRow(expandedRow === index ? null : index);
    setArrowDirection((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleFilterChange = (filter: 'ALL' | 'Connected' | 'Disconnected') => {
    setFilterOption(filter);
  };
  const displayedAssets: HealthScore[] = useMemo(() => {
    let filtered = [...assets];

    // Month filter
    if (selectedMonth) {
      const { monthIndex, year } = parseSelectedMonth(selectedMonth);
      filtered = filtered.filter((asset) => {
        const assetDate = new Date(
          asset.asset_details.squares_recommended_date
        );
        return (
          assetDate.getUTCMonth() === monthIndex &&
          assetDate.getUTCFullYear() === year
        );
      });
    }

    // Connected/Disconnected filter
    if (filterOption === 'Connected') {
      filtered = filtered.filter((asset) => {
        const lastConnected = getLastConnected(
          asset.asset_details.bumper_number
        );
        // console.log(lastConnected, asset);

        return (
          lastConnected &&
          new Date(lastConnected).getTime() >= Date.now() - 24 * 60 * 60 * 1000
        );
      });
    } else if (filterOption === 'Disconnected') {
      filtered = filtered.filter((asset) => {
        const lastConnected = getLastConnected(
          asset.asset_details.bumper_number
        );
        return (
          !lastConnected ||
          new Date(lastConnected).getTime() < Date.now() - 24 * 60 * 60 * 1000
        );
      });
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter((asset) =>
        asset.asset_details.bumper_number
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      );
    }

    // Sorting
    const sorted = filtered.sort((a, b) => {
      // switch (sortOption) {
      //   case 'Manufacturer Date':
      //     return compareDates(
      //       b.asset_details.manufacture_recommended_date,
      //       a.asset_details.manufacture_recommended_date
      //     );
      //   case 'Squares Date':
      //     return compareDates(
      //       b.asset_details.squares_recommended_date,
      //       a.asset_details.squares_recommended_date
      //     );
      //   case 'Health Score':
      //     return compareNumbers(
      //       b.asset_details.health_score,
      //       a.asset_details.health_score
      //     );
      //   default:
      //     return compareNumbers(
      //       a.asset_details.health_score,
      //       b.asset_details.health_score
      //     );
      // }

      // Default: sort by health score ascending
      return compareNumbers(
        b.asset_details.health_score,
        a.asset_details.health_score
      );
    });

    return sorted;
  }, [assets, selectedMonth, filterOption, sortOption, searchQuery]);

  return (
    <Box>
      {/* Toolbar with Search, Button Group, and Sort Dropdown */}
      <Box
        display="flex"
        justifyContent="space-between"
        gap={1}
        alignItems="center"
        p={1}
        sx={{
          backgroundColor: palette.paperBackground,
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          border: `1px solid ${
            enableDarkTheme
              ? ThemePalette.typography.grey
              : ThemePalette.typography.black
          }`,
          borderBottom: '0px',
        }}
      >
        {/* Search Bar */}
        <TextField
          variant="outlined"
          placeholder={t('search_for_an_asset')}
          value={searchQuery}
          onChange={handleSearchChange}
          size="small"
          InputProps={{
            sx: {
              height: '35px',
              minWidth: '100px',
              background: palette.boxBackground,
              color: palette.textPrimary,
              border: `0.5px solid ${
                enableDarkTheme
                  ? ThemePalette.typography.grey
                  : ThemePalette.typography.black
              }`,
              fontSize: '12px',
            },
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon
                  sx={{
                    color: palette.textPrimary,
                  }}
                />
              </InputAdornment>
            ),
          }}
        />

        {/* Button Group */}
        <ButtonGroup>
          {filterOptions.map(({ label, value }) => (
            <Button
              key={value}
              onClick={() => {
                handleFilterChange(value);
              }}
              style={{
                color: filterOption === value ? '#ffffff' : palette.textPrimary,
                background:
                  filterOption === value ? '#007bff' : palette.boxBackground,
                border: `0.5px solid ${
                  enableDarkTheme
                    ? ThemePalette.typography.grey
                    : ThemePalette.typography.black
                }`,
                fontSize: '12px',
              }}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>

        {/* Sort Dropdown */}
        {/* <Selector
          minWidth="80px"
          formControlSx={{ width: '200px' }}
          selectSx={{
            width: '200px',
          }}
          value={sortOption}
          onChange={(event) => {
            setSortOption(String(event.target.value));
          }}
          selectorOptions={SORT_OPTIONS}
          enableDarkTheme={enableDarkTheme}
          height="20px"
        /> */}
      </Box>

      {/* Main Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          borderTopLeftRadius: '0px',
          borderTopRightRadius: '0px',
        }}
      >
        <Table
          style={{
            background: palette.paperBackground,
            border: `1px solid ${
              enableDarkTheme
                ? ThemePalette.typography.grey
                : ThemePalette.typography.black
            }`,
            borderTop: '0px',
            borderBottom: '0px',
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: palette.tableHeader,
              }}
            >
              <TableCell
                style={{
                  color: palette.textPrimary,
                  width: '25%',
                }}
              >
                {t('asset_details')}
              </TableCell>
              <TableCell
                style={{
                  color: palette.textPrimary,
                  width: '25%',
                }}
                align="center"
              >
                {t('health_score')}
              </TableCell>
              <TableCell
                style={{
                  color: palette.textPrimary,
                  width: '25%',
                }}
              >
                {t('age_last_maintenance')}
              </TableCell>
              {/* <TableCell
                style={{
                  color: palette.textPrimary,
                  width: '16.67%',
                }}
              >
                Manufacturer Rec. Date
              </TableCell> */}

              {/* System Recommended Date */}
              {/* <TableCell
                style={{
                  color: palette.textPrimary,
                  width: '15%',
                }}
              >
                Next Maintenance Date
              </TableCell> */}
              <TableCell
                style={{
                  color: palette.textPrimary,
                  width: '25%',
                }}
                align="right"
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedAssets.length > 0 ? (
              displayedAssets.map((asset, index) => (
                <React.Fragment key={asset.asset_details.bumper_number}>
                  <TableRow
                    sx={{
                      border: `1px solid ${
                        enableDarkTheme
                          ? ThemePalette.typography.grey
                          : ThemePalette.typography.black
                      }`,
                    }}
                  >
                    {/* Asset Details */}
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={4}>
                        <Box>
                          <GetIcons
                            icon={getAssetImage(asset.asset_details.asset_type)}
                            enableDarkTheme={enableDarkTheme}
                          />
                        </Box>
                        <Box>
                          <Typography
                            style={{
                              color: palette.textPrimary,
                            }}
                            variant="body2"
                          >
                            {asset.asset_details.bumper_number}
                          </Typography>
                          <Typography
                            style={{
                              color: palette.textPrimary,
                            }}
                            variant="body2"
                          >
                            {asset.asset_details.make} -{' '}
                            {asset.asset_details.model}
                          </Typography>
                          <Typography
                            style={{
                              color: palette.textPrimary,
                            }}
                            variant="body2"
                          >
                            {ASSET_TYPES.find(
                              (e) => e.id === asset.asset_details.asset_type
                            )?.display ?? asset.asset_details.asset_type}
                          </Typography>
                          <Typography
                            style={{
                              color: palette.textPrimary,
                            }}
                            variant="body2"
                            fontSize={8}
                          >
                            {timeAgo(
                              getLastConnected(
                                asset.asset_details.bumper_number
                              )
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    {/* Health Score */}
                    <TableCell
                      onClick={() => {
                        handleRowClick(index);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <HealthScorePie
                          enableDarkTheme={enableDarkTheme}
                          data={healthScorePieRange}
                          value={Number(asset.asset_details.health_score) * 100}
                          width={150}
                          height={100}
                          innerRadius={35}
                          outerRadius={45}
                          needleColor="#d0d000"
                          label={`${(
                            Number(asset.asset_details.health_score) * 100
                          ).toFixed(0)}%`}
                          arrowDirection={arrowDirection[index] ?? false}
                        />
                      </Box>
                    </TableCell>
                    {/* Age */}
                    <TableCell>
                      <Box
                        display="flex"
                        flexDirection="row"
                        // alignItems="flex-start"
                        gap={2}
                      >
                        <Box py={1}>
                          <SVG name="engineLight" color={palette.textPrimary} />
                        </Box>
                        <Box>
                          <Typography
                            style={{
                              color: palette.textPrimary,
                            }}
                            variant="body1"
                          >
                            {Number(asset.asset_details.age_in_days) * 24}{' '}
                            {t('hours')}
                          </Typography>
                          <Typography
                            style={{
                              color: enableDarkTheme
                                ? ThemePalette.typography.lightGrey
                                : ThemePalette.typography.grey,
                            }}
                            variant="subtitle2"
                          >
                            <CalendarToday sx={{ fontSize: '12px' }} />{' '}
                            {convertStdDateString(
                              new Date().setDate(
                                new Date().getDate() -
                                  Number(asset.asset_details.age_in_days)
                              )
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    {/* Manufacturer Rec. Date */}
                    {/* <TableCell>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-start"
                      >
                        <CalendarToday
                          style={{
                            color: enableDarkTheme
                              ? ThemePalette.typography.lightGrey
                              : ThemePalette.typography.grey,
                          }}
                        />
                        <Typography
                          style={{
                            color: palette.textPrimary,
                          }}
                          variant="body1"
                        >
                          {asset.asset_details.manufacture_recommended_date}
                        </Typography>
                        <Typography
                          style={{
                            color: enableDarkTheme
                              ? ThemePalette.typography.lightGrey
                              : ThemePalette.typography.grey,
                          }}
                          variant="subtitle2"
                          color="grey"
                        >
                          {(() => {
                            const daysDifference = Math.ceil(
                              (new Date(
                                asset.asset_details.manufacture_recommended_date
                              ).getTime() -
                                new Date().getTime()) /
                                (1000 * 60 * 60 * 24)
                            );

                            if (daysDifference > 0) {
                              return `${daysDifference} Days from now`;
                            } else if (daysDifference < 0) {
                              return `${Math.abs(daysDifference)} Days overdue`;
                            } else {
                              return 'Immediate';
                            }
                          })()}
                        </Typography>
                      </Box>
                    </TableCell> */}
                    {/* System Rec. Date */}
                    {/* <TableCell>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-start"
                      >
                        <CalendarToday
                          style={{
                            color: enableDarkTheme
                              ? ThemePalette.typography.lightGrey
                              : ThemePalette.typography.grey,
                          }}
                        />
                        <Typography
                          style={{
                            color: palette.textPrimary,
                          }}
                          variant="body1"
                        >
                          {asset.asset_details.squares_recommended_date}
                        </Typography>
                        <Typography
                          style={{
                            color: enableDarkTheme
                              ? ThemePalette.typography.lightGrey
                              : ThemePalette.typography.grey,
                          }}
                          variant="subtitle2"
                          color="grey"
                        >
                          {(() => {
                            const daysDifference = Math.ceil(
                              (new Date(
                                asset.asset_details.squares_recommended_date
                              ).getTime() -
                                new Date().getTime()) /
                                (1000 * 60 * 60 * 24)
                            );

                            if (daysDifference > 0) {
                              return `${daysDifference} Days from now`;
                            } else if (daysDifference < 0) {
                              return `${Math.abs(daysDifference)} Days overdue`;
                            } else {
                              return 'Immediate';
                            }
                          })()}
                        </Typography>
                      </Box>
                    </TableCell> */}
                    {/* Action Button */}
                    <TableCell align="right">
                      <Box display={'flex'} flexDirection={'column'} gap={2}>
                        <Box
                        // sx={{
                        //   marginBottom: '10px',
                        // }}
                        >
                          <Button
                            style={{
                              // minWidth: '220px',
                              // width: '220px',
                              padding: '8px 16px',
                              fontSize: '14px',
                              color: palette.textPrimary,
                              background: palette.paperBackground,
                              border: '1px solid #3666EC',
                              textTransform: 'none',
                            }}
                            onClick={() => {
                              setShowAssetDetails(true);
                              onShowDetails(asset.asset_details.bumper_number);
                            }}
                          >
                            {t('view_details')}
                          </Button>
                        </Box>
                        {/* <Box>
                          <Button
                            style={{
                              minWidth: '220px',
                              width: '220px',
                              padding: '8px 16px',
                              fontSize: '14px',
                              color: palette.textPrimary,
                              background: palette.paperBackground,
                              border: '1px solid #3666EC',
                              textTransform: 'none',
                            }}
                            onClick={() => {
                              onShowDetails(asset.asset_details.bumper_number);
                            }}
                          >
                            Historical Failure Pattern
                          </Button>
                        </Box> */}
                      </Box>
                    </TableCell>
                  </TableRow>
                  {/* Expanded Row for Asset Details */}
                  {expandedRow === index && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          padding: 0,
                          maxWidth: '950px',
                          overflowX: 'auto',
                        }}
                      >
                        <AssetDetailsTable
                          enableDarkTheme={enableDarkTheme}
                          details={asset.component_details}
                          partsHealthComponent={partsHealthComponent}
                          // filteredAssets={assetsDetails}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6}>
                  {assets.length === 0 ? (
                    <Typography
                      style={{
                        color: palette.textPrimary,
                      }}
                      variant="body1"
                    >
                      {t('loading_assets')}
                    </Typography>
                  ) : (
                    <Typography
                      style={{
                        color: palette.textPrimary,
                      }}
                      variant="body1"
                    >
                      {t('no_assets')}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AssetTable;
